import { MultiroleLinkInfo } from '../../../../common/src/ts/types/multiroleLink';

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface NamedPoint {
  readonly name: PointName;
  readonly x: number;
  readonly y: number;
}

export enum PointName {
  None,
  TopLeft,
  TopCenter,
  TopRight,
  MiddleLeft,
  MiddleCenter,
  MiddleRight,
  BottomLeft,
  BottomCenter,
  BottomRight,
  ArrowTip,
  PolygonVertex,
  LineBegin,
  LineEnd,
}

export const unnamedPoint: NamedPoint = {
  name: PointName.None,
  x: 0,
  y: 0,
};

export interface Offset extends Point {
  readonly origin: NamedPoint;
}
export const emptyOffset: Offset = { x: 0, y: 0, origin: unnamedPoint };

export interface TransformDelta extends Offset {
  readonly width: number;
  readonly height: number;
}
export const emptyTransformDelta: TransformDelta = { ...emptyOffset, width: 0, height: 0 };

export interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface Stamp {
  id: string;
  name: string;
  width: number;
  height: number;
  path: string;
}

export enum FontType {
  Gothic,
  Mincho,
  ContentAware,
}

export enum LineType {
  Solid,
  Dotted,
  Dashed,
  None,
}

export enum StampType {
  Default,
  Extra,
}

export type LineCaps = {
  [LineType.Solid]: LineCap,
  [LineType.Dotted]: LineCap,
  [LineType.Dashed]: LineCap,
  [LineType.None]: LineCap,
};

export enum SelectedReflowTabType {
  Reflow,
  SNE,
}

export type LineCap = 'butt' | 'round' | 'square';

export const scalePoint = (orig: Point, scalar: number): Point => {
  return {
    x: orig.x * scalar,
    y: orig.y * scalar,
  };
};

export function isPointEqual(p1: Point, p2: Point): boolean {
  return p1.x === p2.x && p1.y === p2.y;
}

export function getRectCoveringAllPoints(points: ReadonlyArray<Point>): Rect {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const point of points) {
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x);
    maxY = Math.max(maxY, point.y);
  }
  return {
    x: minX,
    y: minY,
    width: Math.abs(maxX - minX),
    height: Math.abs(maxY - minY),
  };
}

export interface Line {
  begin: Point;
  end: Point;
}

export type LineStrip = ReadonlyArray<Point>;

// begin を基準点として x軸 または y軸 に平行な Line を取得する
export function getAxisAlignedLineFromPoints(begin: Point, end: Point): Line {
  const radian = Math.atan2(end.y - begin.y, end.x - begin.x) + Math.PI / 4;

  let axisAlignedEnd;
  if (radian >= 0 && radian < Math.PI / 2) {
    axisAlignedEnd = { x: end.x, y: begin.y };
  } else if (radian >= Math.PI / 2 && radian < Math.PI) {
    axisAlignedEnd = { x: begin.x, y: end.y };
  } else if (radian < 0 && radian > -Math.PI / 2) {
    axisAlignedEnd = { x: begin.x, y: end.y };
  } else {
    axisAlignedEnd = { x: end.x, y: begin.y };
  }

  return { begin, end: axisAlignedEnd };
}

export enum ShapeKind {
  None,
  Rect,
  Square,
  Oval,
  Circle,
  Triangle,
  EquilateralTriangle,
  Sticky,
  Balloon,
  StraightLine,
  AxisAlignedStraightLine,
  Polyline,
  Polygon,
  ImageFile,
  MultiroleLink,
  Stamp,
  LearningPenRect,
}

export type ShapeKindGroupFigure = ShapeKind.Rect | ShapeKind.Square | ShapeKind.Oval |
  ShapeKind.Circle | ShapeKind.Triangle | ShapeKind.EquilateralTriangle | ShapeKind.LearningPenRect;

export type ShapeKindGroupObject = ShapeKind.Rect | ShapeKind.Square | ShapeKind.Oval |
  ShapeKind.Circle | ShapeKind.Triangle | ShapeKind.EquilateralTriangle | ShapeKind.Sticky |
  ShapeKind.Balloon | ShapeKind.StraightLine | ShapeKind.AxisAlignedStraightLine |
  ShapeKind.Polygon | ShapeKind.ImageFile | ShapeKind.MultiroleLink | ShapeKind.Stamp;

export interface NullShape {
  kind: ShapeKind.None;
}

interface LineAttr {
  lineColor: string;
  lineWidth: number;
  lineType: LineType;
  lineCaps: LineCaps;
  alpha: number;
}

export interface DrawingShape extends LineAttr {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Fillable {
  fillColor: string;
  isFilled: boolean;
}

export interface FigureShape extends DrawingShape, Fillable {
  kind: ShapeKindGroupFigure;
}

export interface NoteShape extends DrawingShape, Fillable {
  text: string;
  textColor: string;
  fontType: FontType;
  fontSize: number;
  lineHeight: number;
  padding: number;
  isFolded: boolean;
}

export interface StickyShape extends NoteShape {
  kind: ShapeKind.Sticky;
}

export interface BalloonShape extends NoteShape {
  kind: ShapeKind.Balloon;
  arrowPoint: Point;
}

export interface StraightLineShape extends LineAttr {
  kind: ShapeKind.StraightLine | ShapeKind.AxisAlignedStraightLine;
  line: Line;
}

export interface MultiVertexShape extends LineAttr {
  vertices: ReadonlyArray<Point>;
}

export interface PolylineShape extends MultiVertexShape {
  kind: ShapeKind.Polyline;
}

export interface PolygonShape extends MultiVertexShape, Fillable {
  kind: ShapeKind.Polygon;
}

export interface ImageFileShape {
  kind: ShapeKind.ImageFile;
  pictureID: string;
  x: number;
  y: number;
  width: number;
  height: number;
  alpha: number;
}

export interface StampShape {
  kind: ShapeKind.Stamp;
  stampId: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface MultiroleLinkShape {
  kind: ShapeKind.MultiroleLink;
  x: number;
  y: number;
  multiroleLink: MultiroleLinkInfo;
}

export type ShapeInfo =
  NullShape | FigureShape | StickyShape | BalloonShape | StraightLineShape |
  PolylineShape | PolygonShape | ImageFileShape | MultiroleLinkShape | StampShape;

export const nullShape: Readonly<NullShape> = {
  kind: ShapeKind.None,
};

export enum DrawingMode {
  Disabled,
  Selection,
  Resizing,
  ObjectRemoval,
  Handwriting,
  VerticalStraightLineHandWriting,
  HorizontalStraightLineHandWriting,
  Handerasing,
  RectDrawing,
  SquareDrawing,
  OvalDrawing,
  CircleDrawing,
  TriangleDrawing,
  EquilateralTriangleDrawing,
  StraightLineDrawing,
  AxisAlignedStraightLineDrawing,
  ImagePasting,
  StickyDrawing,
  BalloonDrawing,
  MultiVertexDrawing,
  BalloonArrowMoving,
  PointMoving,
  MultiroleLinkPasting,
  Stamp,
  LearningPenRect,
}

export class Tiling<T> {
  private tiles: { readonly [row: number]: { readonly [column: number]: T } };

  public constructor(tiles: { readonly [row: number]: { readonly [column: number]: T } } = {}) {
    this.tiles = tiles;
  }

  public get isEmpty(): boolean {
    return Object.keys(this.tiles).length === 0;
  }

  public has(row: number, column: number): boolean {
    const { tiles } = this;
    return ((row in tiles) && (column in tiles[row]));
  }

  public at(row: number, column: number): T | undefined {
    const { tiles } = this;
    return ((row in tiles) && (column in tiles[row])) ? tiles[row][column] : undefined;
  }

  public forEach(proc: (row: number, column: number, value: T) => void): void {
    const { tiles } = this;
    for (const row in tiles) {
      for (const column in tiles[row]) {
        proc(+row, +column, tiles[row][column]);
      }
    }
  }

  public plus(row: number, column: number, value: T): Tiling<T> {
    const { tiles } = this;
    const newTiling: { [row: number]: { readonly [column: number]: T } } = { ...tiles };
    const newRow: { [column: number]: T } = (row in tiles) ? { ...tiles[row] } : {};

    newRow[column] = value;
    newTiling[row] = newRow;

    return new Tiling<T>(newTiling);
  }

  public minus(row: number, column: number): Tiling<T> {
    const { tiles } = this;
    if ((row in tiles) && (column in tiles[row])) {
      const newTiling: { [row: number]: { readonly [column: number]: T } } = { ...tiles };
      const newRow: { [column: number]: T } = { ...tiles[row] };
      delete newRow[column];

      if (Object.keys(newRow).length === 0) {
        delete newTiling[row];
      } else {
        newTiling[row] = newRow;
      }

      return new Tiling<T>(newTiling);
    }

    return this;
  }
}
